/**
 * Users + tenants + tenant_users (DB-02 / DB-11).
 *
 * Schema for the multi-tenant identity graph. A user can belong to multiple
 * tenants with an independent role per tenant (DB-02). The tenant_users table
 * supersedes master spec §35's driver_tenant_memberships (DEC-015 — same
 * shape, broader role enum that includes the 10 roles from §6 + LEAD_DRIVER).
 *
 * RLS (CONTEXT D-15):
 *   - users: NO RLS (global identity table — every JWT-authenticated request
 *     resolves a user by id; tenant filtering happens via tenant_users)
 *   - tenants: NO RLS (Phase 3 will use Cognito `custom:tenant_id` claim
 *     filtering at the API layer; declaring RLS on tenants would require
 *     `id = current_setting('app.current_tenant_id')::uuid` which is fine
 *     but redundant with the API-layer guard. Out of Phase 2 scope.)
 *   - tenant_users: RLS on `tenant_id = current_setting('app.current_tenant_id')::uuid`
 *
 * NEGATIVE NOTE — `multi_yard_dispatch` is NOT a column on tenants
 * (DEC-011 / master spec §86). Yard visibility for dispatchers is controlled
 * exclusively by the `dispatcher_yard_assignments` junction table (added by
 * Plan 02-03 in `yards.ts`). Do NOT add `multi_yard_dispatch BOOLEAN` to this
 * table even if a future feature request asks for it — the answer is "assign
 * the dispatcher to all yards" via dispatcher_yard_assignments.
 *
 * Phase 3 / Plan 03 addition (D-04):
 *   - active_tenant_id UUID NULLABLE FK → tenants.id ON DELETE SET NULL.
 *   - Index users_active_tenant_idx for the D-03 auth-resolution JOIN.
 *   - cognito_sub column already exists (Phase 2 line 75); Phase 3 does NOT
 *     re-add it (RESEARCH Pattern 9 verified).
 *
 * Sources:
 *   - master spec §5 (tenant_type 4-value enum)
 *   - master spec §6 (10-role permission matrix)
 *   - master spec §35 (driver_tenant_memberships — consolidated per DEC-015)
 *   - CONTEXT D-02 (drizzle-zod re-exports)
 *   - CONTEXT D-03 (auth-resolution SELECT — uses active_tenant_id)
 *   - CONTEXT D-04 (active_tenant_id column — Phase 3 migration)
 *   - CONTEXT D-15, D-16 (RLS + role split)
 *   - DEC-002 (driver_job_reject_enabled on tenants)
 *   - DEC-015 (tenant_users consolidation)
 *   - RESEARCH Performance Indexes #1-5
 *
 * Locked contracts (future phases):
 *   - tenant_users.role uses text + CHECK (NOT pgEnum) for evolvability.
 *     Adding a role = ALTER CHECK; pgEnum ALTER is fragile (RESEARCH
 *     Open Question #1).
 *   - users.email is NOT NULL — every user has an email (Cognito requires it).
 *   - users.phone is nullable for web users; drivers will have it set in Phase 4.
 */

import {
  boolean,
  check,
  customType,
  index,
  integer,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';

/**
 * `bytea` — drizzle pg-core has no native bytea helper, so we define one. Maps a
 * Postgres `bytea` column to a `Uint8Array` (nullable at each column site). We use
 * `Uint8Array`, NOT Node's `Buffer`: this schema is consumed by the RN/mobile +
 * browser/web builds where `@types/node` is not in scope, so a `Buffer` type ref
 * fails those builds (broke CI Deploy #193). postgres.js still returns a `Buffer`
 * at runtime — a `Uint8Array` subtype — so API reads are unaffected. First binary
 * column in the schema (Phase 10 / Task 10.6 — the tenant print logo).
 */
const bytea = customType<{ data: Uint8Array; notNull: false; default: false }>({
  dataType() {
    return 'bytea';
  },
});
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { TENANT_USER_ROLES, TENANT_USER_STATUSES, appUserRole, tenantTypeEnum } from './enums.js';

/**
 * users — global identity table.
 *
 * NOT tenant-scoped: a user can belong to multiple tenants via tenant_users.
 * NO RLS — every authenticated request resolves a user by id during JWT
 * validation. Email + phone are not platform-private PII (Cognito JWT claims
 * already expose them at login).
 */
export const users = pgTable(
  'users',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    email: text('email').notNull(),
    // nullable — web-only users may not have a phone; drivers always do
    phone: text('phone'),
    // Operator portal PR 11b (0220): the first SMS sign-in proved the phone; cleared when it changes.
    phoneConfirmedAt: timestamp('phone_confirmed_at', { withTimezone: true }),
    // OPP PR 11b (#915 review LOW2): the number phoneConfirmedAt proved; cleared with it.
    phoneConfirmedE164: text('phone_confirmed_e164'),
    fullName: text('full_name').notNull(),
    // nullable until Phase 4 wires Cognito; UNIQUE when set
    cognitoSub: text('cognito_sub'),
    // Migration 0207 (#897 review) — an invite in progress for a person with no account yet: one
    // press claims them before any Cognito call, so two presses never invite the same person twice.
    inviteClaimedAt: timestamp('invite_claimed_at', { withTimezone: true }),
    // D-04 / Phase 3 — currently selected tenant for users with multiple memberships.
    // NULLABLE: brand-new users haven't picked a tenant; tenant deletion via
    // ON DELETE SET NULL clears the pointer without deleting the user (the user
    // can still belong to other tenants via tenant_users — DEC-015).
    // Plan 04 cognito-jwt plugin reads this in the auth-resolution SELECT (D-03);
    // Plan 09 switch-tenant route writes it.
    // RLS: users has NO RLS (Phase 2 D-15) so this column is server-side-only —
    // no client SELECT path exposes it.
    activeTenantId: uuid('active_tenant_id').references(() => tenants.id, {
      onDelete: 'set null',
    }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // soft delete; NULL = active
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    // Phase 9 / D-03 / DEC-004 — user profile timezone for local-by-context display
    // (master spec §44). NOT NULL DEFAULT 'America/Chicago'. The dispatcher map's
    // "as of HH:MM" staleness labels format against this — never UTC. Surfaced to
    // the web client via GET /v1/users/me/profile.
    timezone: text('timezone').notNull().default('America/Chicago'),
    // DEC-224 — the driver's one-time printed signature ("capture once, print
    // forever"). Precomputed ESC/POS GS v 0 raster, exactly like
    // tenants.print_logo_escpos (Task 10.6 / DEC-135): converted ONCE server-side on
    // upload so a field handset hands opaque bytes straight to the Bluetooth printer
    // with no image decoding and no signal. Both NULL until the driver signs.
    //
    // On `users` and not tenant-scoped because a signature belongs to a PERSON — one
    // driver hauling for two haulers signs the same way on both companies' paper.
    // users has NO RLS (header above), and the only routes that touch these columns
    // (/v1/users/me/print-signature) read and write the CALLER's own row; there is
    // deliberately no admin path to set someone else's mark.
    //
    // `bytea` here is the local customType (Uint8Array, never Buffer — see its
    // comment: a Buffer type ref in this file broke CI Deploy #193).
    printSignatureEscpos: bytea('print_signature_escpos'),
    // Drives the mobile cache's staleness check (mirrors print_config.pulled_at).
    // NOT an audit record — audit_events carries that.
    printSignatureUpdatedAt: timestamp('print_signature_updated_at', { withTimezone: true }),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    //
    // No Drizzle-side FK on this column, here or on tenant_users below — the two
    // tables in THIS file are the only such exceptions. imports.ts imports
    // { tenants, users } from this file, so naming importBatches back closes a
    // module cycle. It loads fine at runtime — the reference is a thunk,
    // evaluated after both modules — but tsc cannot type a circular initializer
    // and emits TS7022/TS7024 implicit-any, which is a build error in this repo.
    // The FK is declared in migration SQL instead, and
    // `provenance-marker.test.ts` asserts it in the DATABASE for all seventeen
    // tables, so the declaration styles cannot diverge in effect.
    //
    // users is also the only marked table whose FK stays SINGLE-column. 20.6a /
    // DEC-265 made the other sixteen composite over (tenant_id, import_batch_id)
    // so a row cannot claim another tenant's batch; users has no tenant_id to
    // pair with. DEC-265 records why that asymmetry is acceptable.
    importBatchId: uuid('import_batch_id'),
    /**
     * Migration 0227 — 'OK' | 'BOUNCING' | 'COMPLAINED', set from SES failure events (as
     * site_contacts.email_status, 0152); every sender that mails a user skips a non-OK address.
     * A change of email resets BOUNCING (trigger users_email_status_reset); COMPLAINED stays.
     */
    emailStatus: text('email_status').notNull().default('OK'),
    emailStatusAt: timestamp('email_status_at', { withTimezone: true }),
  },
  (t) => [
    uniqueIndex('users_email_idx').on(t.email),
    // partial UNIQUE because phone is nullable — Postgres unique allows
    // multiple NULLs by default; that's correct behavior for nullable
    // unique columns (RESEARCH perf index #2).
    uniqueIndex('users_phone_idx').on(t.phone),
    uniqueIndex('users_cognito_sub_idx').on(t.cognitoSub),
    // D-03 lookup pattern: cognito-jwt plugin SELECTs by cognito_sub then JOINs
    // tenant_users on (user_id, tenant_id=active_tenant_id). The cognito_sub
    // index is the entry point; this index covers the active_tenant_id filter
    // for the LEFT JOIN.
    index('users_active_tenant_idx').on(t.activeTenantId),
    // Task 20.5 — per-batch reconciliation and undo. Partial because almost no
    // row is import-created; a full index would be mostly NULLs. No tenant_id
    // leading column here, unlike the other sixteen marked tables: users is
    // global.
    index('users_import_batch_idx')
      .on(t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // No pgPolicy — users is a global table (not tenant-scoped). RLS is
    // enforced on tenant_users (the membership graph), not users.
  ],
);

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;
export const userSelectSchema = createSelectSchema(users);
export const userInsertSchema = createInsertSchema(users);

/**
 * tenants — every tenant in the platform (Hauler, Operator, Disposal, Platform).
 *
 * NO RLS on tenants itself. The row IS the tenant; a tenant cannot read its
 * own row through RLS without circular logic (you'd need to know the tenant
 * id to filter to the tenant id). Phase 3 wires Cognito `custom:tenant_id`
 * claim filtering at the API layer.
 *
 * DEC-002: `driver_job_reject_enabled` is the per-tenant feature flag for
 * driver-side reject-job-before-accept. Defaults to false (opt-in).
 *
 * Phase 06.2 / DEC-032 #2 — system-wide billing-clock defaults:
 *   - billingStart: NOT NULL DEFAULT 'ARRIVAL' — existing rows auto-populated;
 *     new rows get ARRIVAL if omitted. CHECK IN('ARRIVAL','START').
 *   - billingEnd: NOT NULL DEFAULT 'COMPLETION' — same pattern.
 *     CHECK IN('DEPARTURE','COMPLETION').
 * When an operator's billingStart/billingEnd is NULL, the capture billing engine
 * (Phase 12) COALESCEs to these tenant-level defaults (DEC-032 #3). The NOT NULL
 * + DEFAULT invariant means COALESCE never falls through to NULL (T-06.2-13).
 */
export const tenants = pgTable(
  'tenants',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    // URL-safe lowercase slug, e.g. 'paelode-systems'
    slug: text('slug').notNull(),
    // display name
    name: text('name').notNull(),
    tenantType: tenantTypeEnum('tenant_type').notNull(),
    // DEC-002 — opt-in tenant flag for driver reject-job-before-accept flow
    driverJobRejectEnabled: boolean('driver_job_reject_enabled').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    deletedAt: timestamp('deleted_at', { withTimezone: true }),
    // Phase 06.2 / DEC-032 #2 — system-wide billing-clock defaults. NOT NULL with
    // DEFAULT so existing rows are auto-populated and COALESCE always resolves
    // (T-06.2-13). Operator-level overrides take precedence when non-NULL.
    billingStart: text('billing_start').notNull().default('ARRIVAL'),
    billingEnd: text('billing_end').notNull().default('COMPLETION'),
    // Phase 07 / D-13 / DEC-036 — per-tenant stale-unsynced alert threshold.
    // Background sync alert (07-09) emits an amber status dot when a driver has
    // been offline and unsynced longer than this many hours. NOT NULL DEFAULT 4
    // so existing rows are safe; positive CHECK (T-07-05) rejects 0 or negative.
    unsyncedAlertThresholdHours: integer('unsynced_alert_threshold_hours').notNull().default(4),
    // Phase 8 / Task 8.2 / DEC-080 — tenant geofence mode (§15 hierarchy).
    // DISABLED → no auto-trigger; ENABLED_VERIFIED_ONLY (default) → only verified
    // sites; ENABLED_ALL → every site with a geofence. CHECK below.
    geofenceMode: text('geofence_mode').notNull().default('ENABLED_VERIFIED_ONLY'),
    // OPEN-220a / DEC-386 — may a geofence MARK ARRIVAL, per site kind? OFF by default:
    // geofence_mode says which sites have geofences; these say whether entering one records
    // ARRIVED_PICKUP / ARRIVED_DISPOSAL (OFF: the phone only logs the presence).
    autoArrivalPickup: boolean('auto_arrival_pickup').notNull().default(false),
    autoArrivalDisposal: boolean('auto_arrival_disposal').notNull().default(false),
    // Phase 8 / Task 8.5 — per-tenant GPS verification target percent (§20). A
    // driver below this on verified/(verified+outside_range) is flagged. Default 90.
    gpsVerificationTargetPercent: integer('gps_verification_target_percent').notNull().default(90),
    // Phase 24 / Task 24.1 (DEC-091) — Paelode-set hauler code (2-4 letters); the
    // hauler prefix in the new HAU-DVN HaulId format (Task 24.4). Nullable until set.
    haulerAbbreviation: text('hauler_abbreviation'),
    // Phase 24 / Task 24.8 (DEC-099) — setup-wizard company-profile fields (step 1).
    dotNumber: text('dot_number'),
    primaryPhone: text('primary_phone'),
    primaryEmail: text('primary_email'),
    businessAddress: text('business_address'),
    // Phase 10 / Task 10.6 (DEC-135) — per-tenant ESC/POS receipt print settings.
    // Both nullable (unset). printLogoEscpos: precomputed ESC/POS raster (GS v 0)
    // logo bytes; printCustomHeader: free-text header line above "lodeX Pro".
    printLogoEscpos: bytea('print_logo_escpos'),
    printCustomHeader: text('print_custom_header'),
    // Migration 0161 (quick 261013-srg PR 0) — web copy of the same uploaded logo,
    // PNG with a 512px longest side, for the web sidebar header. NULL for logos
    // uploaded before 0161 (the original was discarded). updatedAt records when it
    // changed; NULL exactly when the PNG is (CHECK tenants_brand_logo_updated_at_iff_png).
    brandLogoPng: bytea('brand_logo_png'),
    brandLogoUpdatedAt: timestamp('brand_logo_updated_at', { withTimezone: true }),
    // Migration 0174 (task #29) — the hauler's phone printed under the ticket header.
    // NULL prints no phone line. US E.164 (+1XXXXXXXXXX), CHECK tenants_print_phone_e164_us;
    // contact info, never uppercased (DEC-147 exemption).
    printPhone: text('print_phone'),
    // OPP PR 13b dropped operator_tier (DEC-151): operator plans (0212, operator_plans) hold
    // a company's template.
    // Phase 15 / Task 15.4 (DEC-153) — canonical platform operator this OPERATOR
    // tenant represents. NULL for non-OPERATOR tenants; required-at-approve-time for
    // OPERATOR tenants (enforced in approve.ts — not a DB CHECK to avoid breaking
    // existing fixtures). Merged canonical rows are chased at /me read time via
    // platform_operators.merged_into_id.
    // NOTE: FK REFERENCES platform_operators(id) ON DELETE RESTRICT is declared in
    // migration 0062 only. The Drizzle column omits .references() to avoid a circular
    // import cycle (platform-catalog.ts already imports from users.ts). Per DEC-153
    // this is a documented gap in the drizzle-kit schema diff for this column.
    platformOperatorId: uuid('platform_operator_id'),
    // Phase 16 / Task 16.2 (DEC-157) — denormalized reverse-lookup shortcut
    // for DISPOSAL tenants. Mirrors platform_operator_id shape (plain uuid,
    // no FK, indexed). Populated when a DISPOSAL tenant is created via the
    // approve.ts flow. FK is intentionally omitted — disposal_companies.tenant_id
    // → tenants.id is already the referential path; this column is a
    // performance shortcut for the platform admin console. Migration 0064
    // adds the column + index; no CHECK constraint (DISPOSAL is the only
    // tenant_type that populates this — Drizzle diff intentionally shallow).
    disposalCompanyId: uuid('disposal_company_id'),
  },
  (t) => [
    uniqueIndex('tenants_slug_idx').on(t.slug),
    index('tenants_type_idx').on(t.tenantType),
    // Phase 24 / DEC-091 — hauler_abbreviation allowlist (2-4 letters, nullable).
    check(
      'tenants_hauler_abbreviation_check',
      sql`hauler_abbreviation IS NULL OR hauler_abbreviation ~ '^[A-Za-z]{2,4}$'`,
    ),
    // DEC-032 #2 / T-06.2-11 — billing_start/billing_end allowlists (text + CHECK per DEC-024).
    check('tenants_billing_start_check', sql`billing_start IN ('ARRIVAL','START')`),
    check('tenants_billing_end_check', sql`billing_end IN ('DEPARTURE','COMPLETION')`),
    // Phase 07 / D-13 / T-07-05 — positive threshold guard at the DB layer.
    check('tenants_unsynced_threshold_positive', sql`unsynced_alert_threshold_hours > 0`),
    // Phase 8 / DEC-080 — geofence_mode allowlist.
    check(
      'tenants_geofence_mode_check',
      sql`geofence_mode IN ('DISABLED','ENABLED_VERIFIED_ONLY','ENABLED_ALL')`,
    ),
    // Phase 15 / Task 15.4 (DEC-153) — index for platform_operator_id FK lookups.
    index('tenants_platform_operator_id_idx').on(t.platformOperatorId),
    // Phase 16 / Task 16.2 (DEC-157) — index for disposal_company_id reverse
    // lookups from the platform admin console. Migration 0064 pins it.
    index('tenants_disposal_company_id_idx').on(t.disposalCompanyId),
  ],
);

export type Tenant = typeof tenants.$inferSelect;
export type NewTenant = typeof tenants.$inferInsert;
export const tenantSelectSchema = createSelectSchema(tenants);
export const tenantInsertSchema = createInsertSchema(tenants);

/**
 * tenant_setup_wizard — Phase 24 / Task 24.8 (DEC-099). Hybrid of master spec §75
 * (rich per-section model + platform-admin overrides + completion %) and the
 * Task 24.8 spec (hard-gate launch for new haulers). One row per hauler tenant.
 *
 * The 7 sections map to the wizard's config steps:
 *   1 Company · 2 Operators & Customers · 3 Disposal Wells · 4 Yards ·
 *   5 Trucks & Trailers · 6 Drivers · 7 Job Types & Rates.
 * (Step 8 "Review & Launch" is the completion action, not a config section.)
 *
 * Effective completion of a section = section_N_complete OR section_N_admin_override
 * (Paelode may mark a section done when it imported the data on the hauler's behalf).
 *
 * Gate (App.tsx): a TENANT_ADMIN / DISPATCH_MANAGER is redirected into the wizard
 * while NOT all_complete AND NOT wizard_banner_dismissed. "Skip for now" sets
 * wizard_banner_dismissed; "Launch lodeX Pro" sets all_complete + completed_at.
 * RLS-scoped via tenant_id.
 */
export const tenantSetupWizard = pgTable(
  'tenant_setup_wizard',
  {
    tenantId: uuid('tenant_id')
      .primaryKey()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    section1Complete: boolean('section_1_complete').notNull().default(false),
    section2Complete: boolean('section_2_complete').notNull().default(false),
    section3Complete: boolean('section_3_complete').notNull().default(false),
    section4Complete: boolean('section_4_complete').notNull().default(false),
    section5Complete: boolean('section_5_complete').notNull().default(false),
    section6Complete: boolean('section_6_complete').notNull().default(false),
    section7Complete: boolean('section_7_complete').notNull().default(false),
    // §75 platform-admin overrides — set true when Paelode imported the data.
    section1AdminOverride: boolean('section_1_admin_override').notNull().default(false),
    section2AdminOverride: boolean('section_2_admin_override').notNull().default(false),
    section3AdminOverride: boolean('section_3_admin_override').notNull().default(false),
    section4AdminOverride: boolean('section_4_admin_override').notNull().default(false),
    section5AdminOverride: boolean('section_5_admin_override').notNull().default(false),
    section6AdminOverride: boolean('section_6_admin_override').notNull().default(false),
    section7AdminOverride: boolean('section_7_admin_override').notNull().default(false),
    adminOverrideBy: uuid('admin_override_by').references(() => users.id, { onDelete: 'set null' }),
    adminOverrideAt: timestamp('admin_override_at', { withTimezone: true }),
    adminOverrideNotes: text('admin_override_notes'),
    completionPct: integer('completion_pct').notNull().default(0),
    allComplete: boolean('all_complete').notNull().default(false),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    lastSectionViewed: integer('last_section_viewed').notNull().default(1),
    wizardBannerDismissed: boolean('wizard_banner_dismissed').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  () => [
    pgPolicy('tenant_setup_wizard_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type TenantSetupWizard = typeof tenantSetupWizard.$inferSelect;
export type NewTenantSetupWizard = typeof tenantSetupWizard.$inferInsert;
export const tenantSetupWizardSelectSchema = createSelectSchema(tenantSetupWizard);
export const tenantSetupWizardInsertSchema = createInsertSchema(tenantSetupWizard);

/**
 * tenant_users — junction table per DB-02 + DEC-015.
 *
 * RLS-enforced via `tenant_id = current_setting('app.current_tenant_id')::uuid`.
 * 3 indexes from RESEARCH perf #3, #4, #5. The role + status columns are
 * `text` with CHECK constraints (not pgEnum) — see enums.ts docstring.
 *
 * UNIQUE(user_id, tenant_id) — ONE role per membership (DEC-313, migration
 * 0124). This REPLACED the original UNIQUE(user_id, tenant_id, role), whose
 * stated intent was to let a user hold multiple roles in one tenant (e.g.
 * DRIVER + LEAD_DRIVER). Nothing ever exercised that: every writer inserts a
 * single role, the role-change endpoint UPDATEs in place, and every reader
 * treats DRIVER/LEAD_DRIVER as alternatives (`role IN (...)`) rather than as a
 * stack. Meanwhile resolveAuthContext read the role with `LIMIT 1` and no
 * ORDER BY, so a second row resolved a role at random — and the tenant picker
 * (list_user_tenant_memberships) would list the tenant twice. A user who needs
 * two roles' access now gets a COMBINED ROLE CODE whose permission set is the
 * exact union (see auth/permissions.ts COMBINED_ROLE_COMPONENTS), which is one
 * row and one resolvable role.
 */
export const tenantUsers = pgTable(
  'tenant_users',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // CHECK constraint defined below in the (t) => [...] callback.
    role: text('role').notNull(),
    // CHECK constraint defined below in the (t) => [...] callback.
    status: text('status').notNull().default('ACTIVE'),
    joinedAt: timestamp('joined_at', { withTimezone: true }).notNull().defaultNow(),
    offboardedAt: timestamp('offboarded_at', { withTimezone: true }),
    // Migration 0200 (operator portal rework) — when the member first reached the operator portal
    // (the first GET /v1/operator-portal/me). NULL = invited, never signed in. A Paelode admin's
    // is stamped by GET /v1/users/me/profile, the admin console's load (Paelode team page).
    activatedAt: timestamp('activated_at', { withTimezone: true }),
    // Migration 0251 (Paelode team page) — when a Paelode admin last loaded the admin console,
    // written by GET /v1/users/me/profile at most every 15 minutes. NULL = not seen since 0251.
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker. Bare uuid for the same module-cycle reason
    // documented on users.import_batch_id above. Unlike users, tenant_users DOES
    // have a tenant_id, so 0107 (20.6a / DEC-265) gives it the COMPOSITE FK over
    // (tenant_id, import_batch_id) — it just cannot be declared here.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Perf indexes (RESEARCH #3, #4, #5)
    index('tenant_users_user_idx').on(t.userId),
    // Task 20.5 — per-batch reconciliation and undo.
    index('tenant_users_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    index('tenant_users_tenant_role_idx').on(t.tenantId, t.role),
    // DEC-313 / migration 0124 — one role per (user, tenant). See the table
    // docstring above for why the third column went away.
    uniqueIndex('tenant_users_user_tenant_idx').on(t.userId, t.tenantId),
    // CHECK constraints — typed `check()` from drizzle-orm/pg-core 0.45.x.
    // role and status allowlists pulled from the pinned consts in enums.ts so
    // adding a value = update enums.ts + ALTER CHECK migration (no drift).
    check(
      'tenant_users_role_check',
      sql.raw(`role IN (${TENANT_USER_ROLES.map((r) => `'${r}'`).join(', ')})`),
    ),
    check(
      'tenant_users_status_check',
      sql.raw(`status IN (${TENANT_USER_STATUSES.map((s) => `'${s}'`).join(', ')})`),
    ),
    // RLS — CONTEXT D-15. T-02-01-01 (HIGH) — Postgres physically returns
    // 0 rows on cross-tenant SELECT for app_user. Proven by Plan 02-06's
    // RLS-isolation test (the SC2 canary).
    pgPolicy('tenant_users_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type TenantUser = typeof tenantUsers.$inferSelect;
export type NewTenantUser = typeof tenantUsers.$inferInsert;
export const tenantUserSelectSchema = createSelectSchema(tenantUsers);
export const tenantUserInsertSchema = createInsertSchema(tenantUsers);
